import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

const NEW_LOGO_URL = "https://customer-assets.emergentagent.com/job_krazy-bets-preview/artifacts/pvmmvvvw__krazy%20bets%20logo%20Final%20png1.png";

const ResetPasswordPage = () => {
  const [step, setStep] = useState(1); // 1=enter email, 2=enter code+new pw, 3=done
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/request-password-reset', { email });
      toast.success('Reset code sent to your email!');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { email, reset_code: resetCode, new_password: newPassword });
      toast.success('Password reset successfully!');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A]">
      <Header showAuth={false} />
      <div className="flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="card-dark">
            <div className="text-center mb-8">
              <img src={NEW_LOGO_URL} alt="KrazyBets" className="h-16 w-16 mx-auto mb-3" />
              <h1 className="text-2xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }}>
                Reset <span className="text-[#D4AF37]">Password</span>
              </h1>
            </div>

            {step === 1 && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <p className="text-gray-400 text-sm text-center mb-4">Enter your email and we'll send you a reset code.</p>
                <div>
                  <Label className="font-semibold text-white text-sm">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="input-dark" style={{ paddingLeft: '3rem' }} required data-testid="reset-email-input" />
                  </div>
                </div>
                <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="send-reset-btn">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-gray-400 text-sm text-center mb-4">Enter the reset code sent to <span className="text-[#D4AF37]">{email}</span></p>
                <div>
                  <Label className="font-semibold text-white text-sm">Reset Code</Label>
                  <Input value={resetCode} onChange={(e) => setResetCode(e.target.value)} placeholder="Enter 6-digit code" className="input-dark mt-1 text-center text-lg tracking-widest" maxLength={6} required data-testid="reset-code-input" />
                </div>
                <div>
                  <Label className="font-semibold text-white text-sm">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" className="input-dark" style={{ paddingLeft: '3rem' }} required data-testid="new-password-input" />
                  </div>
                </div>
                <div>
                  <Label className="font-semibold text-white text-sm">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" className="input-dark" style={{ paddingLeft: '3rem' }} required data-testid="confirm-new-password-input" />
                  </div>
                </div>
                <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="reset-password-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center">
                <CheckCircle2 className="h-16 w-16 mx-auto text-[#28A745] mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Password Reset!</h2>
                <p className="text-gray-400 mb-6">Your password has been updated. You can now login with your new password.</p>
                <Link to="/login">
                  <Button className="btn-primary w-full" data-testid="go-to-login-btn">Go to Login</Button>
                </Link>
              </div>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-gray-400 text-sm flex items-center justify-center gap-2 hover:text-[#D4AF37]">
                <ArrowLeft className="h-4 w-4" /> Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
